import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./ibloom/styles";
import { productAlt } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import ShareButton from "./ibloom/mapbazzar/ShareButton";
import { openWhatsApp } from "@/lib/ibloomConfig";

/**
 * Public single-shop page (no auth) — used by the share link sent over WhatsApp.
 * Shows the shop's products and a one-tap WhatsApp CTA so visitors can message
 * the shopkeeper directly. URL: /shop/{slug} (old /shop/{uuid} links resolve too
 * and client-canonicalise to the slug URL).
 *
 * Tapping any product tile opens a product detail modal with the full image,
 * description, price, and a one-tap "Chat on WhatsApp" / "View on Mapbazar"
 * call-to-action. This is the public catalogue — no login required to browse.
 */
export default function PublicShop() {
  const { slug, city, shopSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [shopQuery, setShopQuery] = useState("");
  const [addedFlash, setAddedFlash] = useState("");
  const { add: addToCart, count: cartCount } = useCart();

  useEffect(() => {
    let active = true;
    setLoading(true);
    const byPath = Boolean(city && shopSlug);
    const url = byPath ? `/shops/by-path/${city}/${shopSlug}` : `/shops/by-slug/${slug}`;
    api.get(url)
      .then((r) => {
        if (!active) return;
        setShop(r.data?.shop);
        setProducts(r.data?.products || []);
        const canonical = r.data?.seo_path;
        const current = byPath ? `/${city}/${shopSlug}` : `/shop/${slug}`;
        if (canonical && canonical !== current) {
          const qs = searchParams.toString();
          navigate(`${canonical}${qs ? `?${qs}` : ""}`, { replace: true });
        }
      })
      .catch(() => active && setErr("Shop not found"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug, city, shopSlug, navigate, searchParams]);

  // Auto-open the product modal when a shared deep-link includes ?product=ID.
  useEffect(() => {
    const pid = searchParams.get("product");
    if (!pid || !products.length) return;
    const match = products.find((p) => p.id === pid);
    if (match) setSelected(match);
  }, [products, searchParams]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading shop…</div>;
  if (err || !shop) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>🏬</div>
      <div style={{ fontSize: 14, color: "#888" }}>{err || "Shop not found"}</div>
      <button onClick={() => navigate("/")} style={{ marginTop: 14, padding: "10px 18px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }}>Back to Mapbazar</button>
    </div>
  );

  const wa = (shop.phone || "").replace(/\D/g, "");
  const waDigits = wa.length === 10 ? `91${wa}` : wa;

  const shown = products.filter(
    (p) => !shopQuery.trim() || (p.name || "").toLowerCase().includes(shopQuery.trim().toLowerCase())
  );
  const handleAddToCart = (p) => {
    addToCart({ ...p, shop_id: shop.id, shop_name: shop.name, shop_phone: shop.phone });
    setAddedFlash(p.name);
    setTimeout(() => setAddedFlash(""), 1600);
  };

  const shareShop = async () => {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://mapbazar.com";
    const url = `${origin}${shop.seo_path || `/shop/${shop.slug || shop.id}`}`;
    const text = `🏬 ${shop.name} is now on Mapbazar! Browse my products and order with 30-min home delivery.\n${url}`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: shop.name, text, url }); return; } catch (_) {}
    }
    const waLink = `https://wa.me/?text=${encodeURIComponent(text)}`;
    openWhatsApp(waLink);
  };

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", minHeight: "100dvh", background: "#FAFAF5" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", background: "white", borderBottom: "1px solid #EFEDE3",
      }}>
        <a href="/" data-testid="public-shop-home-logo" aria-label="Mapbazar home"
          style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          <img src="/mapbazar-logo.png" alt="Mapbazar" style={{ height: 26, width: "auto", display: "block" }} />
        </a>
        <button
          type="button"
          data-testid="public-shop-share-btn"
          onClick={shareShop}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "7px 14px", borderRadius: 100, border: "1px solid #25D366",
            background: "white", color: "#1A7F4E", fontWeight: 700, fontSize: 12.5,
            cursor: "pointer",
          }}
        >
          📤 Share my shop
        </button>
      </div>
      <div style={{
        background: "linear-gradient(135deg, #FFF9F0, #FDE7C8)",
        padding: "28px 22px 22px",
        borderBottom: "1px solid #F3CC8B",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 58, height: 58, borderRadius: 14, background: "linear-gradient(135deg, #C97B3C, #8B1A4A)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🏬</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.25 }} data-testid="public-shop-name">{shop.name}</div>
            <div style={{ fontSize: 12.5, color: "#666", marginTop: 3 }}>📍 {shop.address || "Local · Ahmedabad"}</div>
          </div>
        </div>
        <div style={{ marginTop: 14, fontSize: 12.5, color: "#5C3D12", lineHeight: 1.55 }}>
          Local shop on Mapbazar — every purchase quietly funds family preventive care.
        </div>
        {waDigits && (
          <a
            href={`https://wa.me/${waDigits}?text=${encodeURIComponent(`Hi ${shop.name}, I saw your shop on Mapbazar.`)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="public-shop-whatsapp"
            style={{
              marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "12px", borderRadius: 100,
              background: "#25D366", color: "white", fontWeight: 700, fontSize: 14,
              textDecoration: "none", boxShadow: "0 6px 16px rgba(37,211,102,0.3)",
            }}>
            💬 Chat on WhatsApp
          </a>
        )}
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#0F3D2E", fontWeight: 700, marginBottom: 10 }}>
          LISTINGS · {products.length}
        </div>
        {products.length > 0 && (
          <input
            data-testid="shop-search-input"
            value={shopQuery}
            onChange={(e) => setShopQuery(e.target.value)}
            placeholder="Search in this shop..."
            style={{ width: "100%", marginBottom: 12, padding: "11px 14px", borderRadius: 100, border: "1.5px solid #E0DED2", fontSize: 14, outline: "none", boxSizing: "border-box", background: "white" }}
          />
        )}
        {products.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", background: "white", border: "1px dashed #D5D3C7", borderRadius: 14, color: "#888", fontSize: 13 }}>
            This shop hasn't listed any products yet.
          </div>
        ) : shown.length === 0 ? (
          <div data-testid="shop-search-empty" style={{ padding: 24, textAlign: "center", background: "white", border: "1px dashed #D5D3C7", borderRadius: 14, color: "#888", fontSize: 13 }}>
            No products match "{shopQuery}".
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            {shown.map((p) => (
              <div
                key={p.id}
                data-testid={`public-shop-product-${p.id}`}
                onClick={() => setSelected(p)}
                style={{
                  background: "white", border: "1px solid #EAE8DC", borderRadius: 14,
                  overflow: "hidden", textAlign: "left", cursor: "pointer",
                }}
              >
                <div style={{ width: "100%", aspectRatio: "1/1", background: "#F6FAF7", position: "relative" }}>
                  {p.image && <img src={resolveImageSrc(p.image)} alt={productAlt(p.name, shop.name, shop.address)} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", filter: p.in_stock === false ? "grayscale(60%)" : "none" }} />}
                  {p.in_stock === false && (
                    <span data-testid={`public-shop-oos-${p.id}`} style={{
                      position: "absolute", top: 6, left: 6,
                      background: "rgba(180,69,42,0.95)", color: "white",
                      fontSize: 9, fontWeight: 800, padding: "3px 7px", borderRadius: 100, letterSpacing: 0.4,
                    }}>OUT OF STOCK</span>
                  )}
                  <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: 6, right: 6 }}>
                    <ShareButton product={{ ...p, shop_id: shop.id, shop_name: shop.name }} variant="icon" testIdSuffix={`public-${p.id}`} />
                  </div>
                </div>
                <div style={{ padding: "8px 10px 10px" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.name}>{p.name}</div>
                  {p.pack_size && <div data-no-translate style={{ fontSize: 10, color: "#8A94A6", marginTop: 1 }}>{p.pack_size}</div>}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexWrap: "wrap" }}>
                      <span style={{ ...S.fraunces, fontSize: 15, fontWeight: 800, color: Number(p.mrp) > Number(p.price) ? "#8B1A4A" : "#0F3D2E" }}>₹{p.price}</span>
                      {Number(p.mrp) > Number(p.price) && <span data-no-translate style={{ fontSize: 11, color: "#9AA4B7", textDecoration: "line-through" }}>₹{p.mrp}</span>}
                    </div>
                    {p.in_stock !== false && (
                      <button
                        type="button"
                        data-testid={`public-shop-add-cart-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(p); }}
                        aria-label="Add to cart"
                        style={{ width: 30, height: 30, borderRadius: 100, border: "1.5px solid #C97B3C", background: "#FFF5EA", color: "#8B1A4A", fontSize: 14, cursor: "pointer", flexShrink: 0 }}
                      >🛒</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", padding: 22, fontSize: 11, color: "#888" }}>
        <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#0F3D2E", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
          → Explore more shops on Mapbazar
        </button>
      </div>

      {addedFlash && (
        <div data-testid="public-shop-added-flash" style={{ position: "fixed", left: "50%", bottom: 84, transform: "translateX(-50%)", zIndex: 1200, background: "#0F3D2E", color: "white", padding: "10px 16px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, boxShadow: "0 8px 20px rgba(0,0,0,0.25)" }}>
          🛒 Added "{addedFlash}"
        </div>
      )}
      {cartCount > 0 && (
        <button
          data-testid="public-shop-view-cart"
          onClick={() => { try { sessionStorage.setItem("open_cart", "1"); } catch (_) {} navigate("/app"); }}
          style={{ position: "fixed", right: 16, bottom: 84, zIndex: 1200, padding: "12px 18px", borderRadius: 100, border: "none", background: "#C97B3C", color: "white", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 10px 24px rgba(201,123,60,0.4)" }}
        >
          🛒 Cart · {cartCount} → Checkout
        </button>
      )}

      {selected && (
        <PublicProductDetail
          product={selected}
          shop={shop}
          waDigits={waDigits}
          onClose={() => setSelected(null)}
          onBuyNow={() => {
            try { sessionStorage.setItem("pending_buy_product_id", selected.id); } catch (_) {}
            navigate("/");
          }}
        />
      )}
    </div>
  );
}

function PublicProductDetail({ product, shop, waDigits, onClose, onBuyNow }) {
  const waMsg = `Hi ${shop.name || "there"}! I saw "${product.name}" (₹${product.price}) on your Mapbazar catalogue. 🙏`;
  const waUrl = waDigits && waDigits.length >= 10 ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waMsg)}` : null;
  return (
    <div
      data-testid="public-product-detail-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.55)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: "22px 22px 0 0", maxWidth: 480, width: "100%", height: "92dvh", maxHeight: "92dvh", overflowY: "auto", position: "relative", WebkitOverflowScrolling: "touch" }}
      >
        <button
          data-testid="public-product-detail-close-btn"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "sticky", top: 10, marginLeft: "calc(100% - 50px)", marginBottom: -44,
            zIndex: 10, width: 38, height: 38, borderRadius: "50%",
            background: "rgba(255,255,255,0.96)", color: "#1A1A1A",
            border: "1px solid #EAE8DC", boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(6px)",
          }}
        >✕</button>
        <div style={{ width: 40, height: 4, background: "#E0DED2", borderRadius: 4, margin: "10px auto 0", position: "relative", top: -28 }} />
        {(() => {
          const gallery = Array.isArray(product.images) && product.images.length > 0
            ? product.images
            : [product.image].filter(Boolean);
          const main = resolveImageSrc(gallery[0]);
          const extras = gallery.slice(1).map(resolveImageSrc).filter((s) => s && (s.startsWith("http") || s.startsWith("/api/") || s.startsWith("data:")));
          return (
            <>
              {main && (main.startsWith("http") || main.startsWith("/api/") || main.startsWith("data:")) ? (
                <img src={main} alt={productAlt(product.name, shop.name, shop.address)} style={{ width: "100%", height: 280, objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "100%", height: 220, background: "#F6FAF7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, color: "#bbb" }}>📦</div>
              )}
              {extras.length > 0 && (
                <div data-testid="public-product-detail-gallery"
                  style={{ display: "flex", gap: 8, padding: "10px 16px 0", overflowX: "auto", WebkitOverflowScrolling: "touch" }}
                >
                  {extras.map((src, i) => (
                    <img key={i} src={src} alt={`extra-${i}`}
                      style={{ flexShrink: 0, width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1px solid #EAE8DC" }} />
                  ))}
                </div>
              )}
            </>
          );
        })()}
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C97B3C", fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
                {product.category || "Local"}
              </div>
              <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, lineHeight: 1.2, marginBottom: 6 }} data-testid="public-product-detail-name">{product.name}</div>
              {product.pack_size && (
                <div data-testid="public-product-detail-pack-size" data-no-translate style={{ display: "inline-block", fontSize: 11, fontWeight: 800, color: "#0F3D2E", background: "#F0EFE8", borderRadius: 100, padding: "3px 10px", marginBottom: 6 }}>📦 {product.pack_size}</div>
              )}
              {product.description && (
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.55 }}>{product.description}</div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              {Number(product.mrp) > Number(product.price) ? (
                <>
                  <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 800, color: "#8B1A4A" }} data-testid="public-product-detail-price">₹{product.price}</div>
                  <div data-no-translate style={{ fontSize: 13, color: "#9AA4B7", textDecoration: "line-through", lineHeight: 1 }}>₹{product.mrp}</div>
                  <div data-no-translate style={{ marginTop: 3, display: "inline-block", background: "#1C6B4A", color: "white", fontSize: 10, fontWeight: 900, padding: "1px 6px", borderRadius: 100 }}>{Math.round((1 - Number(product.price) / Number(product.mrp)) * 100)}% OFF</div>
                </>
              ) : (
                <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 800, color: "#0F3D2E" }} data-testid="public-product-detail-price">₹{product.price}</div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 14, background: "#FAFAF5", borderRadius: 12, border: "1px solid #EAE8DC" }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#888", fontWeight: 700, marginBottom: 4 }}>SHOP</div>
            <a
              href={shop.seo_path || `/shop/${shop.slug || shop.id}`}
              data-testid="public-product-detail-shop-link"
              style={{ fontSize: 14, fontWeight: 700, color: "#0F3D2E", textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" }}
            >
              {shop.name} →
            </a>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>📍 {shop.address || "Nearby"}</div>
            {waUrl && <div style={{ fontSize: 12, color: "#25D366", marginTop: 4, fontWeight: 700 }}>💬 WhatsApp available</div>}
          </div>

          <button
            data-testid="public-product-buy-now-btn"
            onClick={onBuyNow}
            disabled={product.in_stock === false}
            style={{
              marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: 14, borderRadius: 100, border: "none",
              background: product.in_stock === false ? "#999" : "#0F3D2E", color: "white", fontWeight: 800, fontSize: 14,
              cursor: product.in_stock === false ? "not-allowed" : "pointer",
              boxShadow: product.in_stock === false ? "none" : "0 8px 20px rgba(15,61,46,0.35)",
            }}
          >
            {product.in_stock === false ? "🚫 Currently out of stock" : `🛵 Buy Now · ₹${product.price} COD · 30-min delivery`}
          </button>

          {waUrl && (
            <a
              href={waUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid="public-product-whatsapp-btn"
              style={{
                marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                width: "100%", padding: 12, borderRadius: 100, border: "1px solid #25D366",
                background: "white", color: "#1A7F4E", fontWeight: 700, fontSize: 13,
                cursor: "pointer", textDecoration: "none",
              }}
            >
              💬 Chat with {shop.name?.split(" ")?.[0] || "shopkeeper"} on WhatsApp
            </a>
          )}

          <ShareButton product={{ ...product, shop_id: shop.id, shop_name: shop.name }} variant="pill" testIdSuffix="public-product-detail" />

          <button
            onClick={onClose}
            style={{ marginTop: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", color: "#333", fontWeight: 600, cursor: "pointer" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
